import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CreditCard, ArrowLeft, ArrowRight } from "lucide-react";
import { createCheckout } from "@/lib/api";

export default function Pay() {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const fieldCls =
    "w-full bg-transparent border-b border-white/20 pb-3 pt-1 text-[#F5F5F0] placeholder:text-[#5a5a5a] focus:border-gold focus:outline-none transition-colors duration-300";

  const pay = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter the amount you were quoted."); return; }
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        amount: amt,
        description: note || "Fare payment",
        origin_url: window.location.origin,
      });
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="pay-page">
      <div className="noise-overlay" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md border border-white/10 bg-surface p-10 lg:p-12"
      >
        <a href="/" className="inline-flex items-center gap-2 text-[#8A8A8A] hover:text-gold transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" /><span className="overline text-[#8A8A8A]">Back to site</span>
        </a>
        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="w-4 h-4 text-gold" />
          <p className="overline">Secure Payment</p>
        </div>
        <h1 className="font-serif text-4xl tracking-tight mb-3">Pay your fare</h1>
        <p className="text-[#8A8A8A] font-light mb-8 text-sm">
          Enter the fare you were quoted and pay securely by card, Apple Pay or Google Pay.
        </p>
        <form onSubmit={pay} className="space-y-7" data-testid="pay-form">
          <div>
            <label className="overline text-[#8A8A8A] block mb-2">Amount (USD)</label>
            <div className="flex items-center gap-2">
              <span className="font-serif text-3xl text-gold">$</span>
              <input type="number" min="1" step="1" data-testid="pay-amount" className={`${fieldCls} text-2xl`}
                value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
            </div>
          </div>
          <div>
            <label className="overline text-[#8A8A8A] block mb-2">Reference (optional)</label>
            <input data-testid="pay-note" className={fieldCls} value={note}
              onChange={(e) => setNote(e.target.value)} placeholder="e.g. Southampton to JFK, Apr 1" />
          </div>
          <button type="submit" disabled={loading} data-testid="pay-submit-btn"
            className="group w-full flex items-center justify-center gap-3 bg-gold text-black py-4 hover:tracking-widest hover:bg-gold-hover transition-[letter-spacing,background-color] duration-500 disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            <span className="overline text-black">{loading ? "Redirecting" : "Continue to Payment"}</span>
            {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
